import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Bell, CheckCircle, MessageSquare, 
  ThumbsUp, Check, Trash2, X, RefreshCw 
} from 'lucide-react';
import toast from 'react-hot-toast';

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  // Fetch notification count on initial render and set up polling
  useEffect(() => {
    fetchNotificationCount();
    
    // Poll for new notifications every 60 seconds
    const interval = setInterval(() => {
      fetchNotificationCount();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Handle outside clicks to close the dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Fetch notifications when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);
  
  const fetchNotificationCount = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/count`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch notification count');
      }
      
      const data = await response.json();
      setUnreadCount(data.count);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };
  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications?limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      
      const data = await response.json();
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }
      
      // Update notifications list
      setNotifications(notifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true } 
          : notification
      ));
      
      // Update unread count
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };
  
  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }
      
      // Update notifications list
      setNotifications(notifications.map(notification => ({ ...notification, isRead: true })));
      
      // Update unread count
      setUnreadCount(0);
      
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };
  
  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }
      
      // Remove notification from list
      setNotifications(notifications.filter(notification => notification.id !== notificationId));
      
      // Update unread count if the notification was unread
      const wasUnread = notifications.find(n => n.id === notificationId)?.isRead === false;
      if (wasUnread) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };
  
  const handleNotificationClick = (notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    
    // Parse metadata if available
    let metadata = {};
    try {
      if (notification.metadata) {
        metadata = JSON.parse(notification.metadata);
      }
    } catch (error) {
      console.error('Error parsing notification metadata:', error);
    }
    
    // Navigate based on notification type
    const { type, referenceId, referenceType } = notification;
    
    if (metadata.courseId) {
      if (referenceType === 'question' || type.includes('question_vote')) {
        // Direct link to the question - use referenceId which is the question ID
        navigate(`/courses/${metadata.courseId}/questions/${referenceId}`);
      } else if (referenceType === 'answer' || type.includes('answer')) {
        // For answers, we need to navigate to the question that contains the answer
        // Important: Use metadata.questionId, NOT the referenceId (which is the answer ID)
        if (metadata.questionId) {
          navigate(`/courses/${metadata.courseId}/questions/${metadata.questionId}`);
        } else {
          // Fallback to notifications page if we don't have the question ID
          console.warn('Missing questionId in notification metadata for answer reference:', notification);
          navigate('/notifications');
          toast.warning('Could not navigate to the specific content');
        }
      } else {
        // Default to notifications page
        navigate('/notifications');
      }
    } else {
      // Default to notifications page if no metadata
      console.warn('Missing courseId in notification metadata:', notification);
      navigate('/notifications');
      toast.warning('Could not navigate to the specific content');
    }
    
    // Close dropdown
    setIsOpen(false);
  };
  
  // Render notification icon based on type
  const renderNotificationIcon = (notification) => {
    const { type } = notification;
    
    if (type === 'answer_accepted') {
      return <CheckCircle size={16} className="text-green-500 dark:text-green-400" />;
    } else if (type.includes('answer')) {
      return <MessageSquare size={16} className="text-blue-500 dark:text-blue-400" />;
    } else if (type.includes('vote')) {
      return <ThumbsUp size={16} className="text-red-500 dark:text-red-400" />;
    } else {
      return <Bell size={16} className="text-gray-500 dark:text-gray-400" />;
    }
  };
  
  // Format timestamp to relative time (e.g., "2 hours ago")
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }
    
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  };

  // Helper function to check if notification has rejection notes
  const hasRejectionNotes = (notification) => {
    if (!notification.type.includes('rejected')) return false;
    
    try {
      const metadata = JSON.parse(notification.metadata || '{}');
      return metadata.rejectionNotes && metadata.rejectionNotes.trim().length > 0;
    } catch (error) {
      return false;
    }
  };

  // Helper function to get rejection notes
  const getRejectionNotes = (notification) => {
    try {
      const metadata = JSON.parse(notification.metadata || '{}');
      return metadata.rejectionNotes;
    } catch (error) {
      return null;
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500"
      >
        <Bell size={20} className="text-gray-600 dark:text-gray-400" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-[9999] border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Notifications
            </h3>
            <div className="flex space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="p-1 text-xs text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center"
                  title="Mark all as read"
                >
                  <Check size={14} className="mr-1" />
                  <span className="hidden sm:inline">Mark all read</span>
                </button>
              )}
              <button
                onClick={fetchNotifications}
                className={`p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 ${loading ? 'animate-spin' : ''}`}
                title="Refresh"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
          
          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <div className="w-5 h-5 border-t-2 border-b-2 border-red-500 rounded-full animate-spin mr-2"></div>
                <span className="text-gray-600 dark:text-gray-400 text-sm">Loading notifications...</span>
              </div>
            ) : error ? (
              <div className="py-8 text-center">
                <p className="text-red-500 text-sm mb-2">{error}</p>
                <button
                  onClick={fetchNotifications}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  Try Again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell size={24} className="text-gray-400 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400 text-sm">No notifications yet</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map(notification => (
                  <li 
                    key={notification.id} 
                    className={`relative ${notification.isRead ? 'bg-white dark:bg-gray-800' : 'bg-blue-50 dark:bg-blue-900/20'} hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors`}
                  >
                    <div
                      onClick={() => handleNotificationClick(notification)}
                      className="px-4 py-3 cursor-pointer"
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                          {renderNotificationIcon(notification)}
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm text-gray-800 dark:text-gray-200">
                            {notification.message}
                          </p>
                          
                          {/* Rejection Notes */}
                          {hasRejectionNotes(notification) && (
                            <div className="mt-2 pl-3 border-l-2 border-gray-300 dark:border-gray-600">
                              <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                                Reason: {getRejectionNotes(notification)}
                              </p>
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatRelativeTime(notification.createdAt)}
                          </p>
                        </div>
                        <div className="flex-shrink-0 flex">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id);
                              }}
                              className="p-1 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 rounded-full"
                              title="Mark as read"
                            >
                              <Check size={14} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                            title="Delete notification"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-2 text-center">
            <Link
              to="/notifications"
              className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
              onClick={() => setIsOpen(false)}
            >
              View All Notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;